import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Building2, MapPin, Clock, Users, Wrench, Briefcase, Phone, Bell,
  MessageSquare, FileText, CreditCard, Upload, UserPlus, CalendarPlus,
  CheckCircle2, Circle, ChevronLeft, ChevronRight, X, PartyPopper,
  ExternalLink, RefreshCw, Loader2, Sparkles, MinusCircle,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const STEP_ICONS = {
  business_info: Building2, locations: MapPin, business_hours: Clock,
  team: Users, services: Wrench, job_settings: Briefcase, phone: Phone,
  notifications: Bell, templates: MessageSquare, invoice_settings: FileText,
  stripe: CreditCard, import: Upload, first_customer: UserPlus, first_job: CalendarPlus,
};

const CATEGORY_BADGE = {
  required: { label: "Required", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
  recommended: { label: "Recommended", cls: "bg-blue-500/10 text-blue-500 border-blue-500/20" },
  optional: { label: "Optional", cls: "bg-muted text-muted-foreground border-border" },
};

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu",
];

const authCfg = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

/* ─── Embedded: Business Information form ─── */
const BusinessInfoForm = ({ onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings/business`, authCfg())
      .then((r) => setForm({
        business_name: r.data.business_name || "",
        business_type: r.data.business_type || "locksmith",
        business_email: r.data.business_email || "",
        business_phone: r.data.business_phone || "",
        website: r.data.website || "",
        business_address: r.data.business_address || "",
        city: r.data.city || "",
        state: r.data.state || "",
        zip_code: r.data.zip_code || "",
        timezone: r.data.timezone || "America/New_York",
        currency: r.data.currency || "USD",
      }))
      .catch(() => setForm({}));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.business_name?.trim()) { toast.error("Business name is required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/settings/business`, form, authCfg());
      toast.success("Business information saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };
  if (!form) return <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-3" data-testid="onb-business-info-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Business Name *</Label>
          <Input data-testid="onb-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Acme Home Services" /></div>
        <div><Label className="text-xs">Industry</Label>
          <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
            <SelectTrigger data-testid="onb-business-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["locksmith", "garage_door", "hvac", "plumbing", "electrical", "appliance_repair", "cleaning", "chimney", "landscaping", "pest_control", "handyman", "other"].map((t) => (
                <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
              ))}
            </SelectContent>
          </Select></div>
        <div><Label className="text-xs">Business Email</Label>
          <Input value={form.business_email} onChange={(e) => set("business_email", e.target.value)} /></div>
        <div><Label className="text-xs">Business Phone</Label>
          <Input value={form.business_phone} onChange={(e) => set("business_phone", e.target.value)} placeholder="(555) 123-4567" /></div>
        <div className="sm:col-span-2"><Label className="text-xs">Website</Label>
          <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" /></div>
        <div className="sm:col-span-2"><Label className="text-xs">Street Address</Label>
          <Input data-testid="onb-business-address" value={form.business_address} onChange={(e) => set("business_address", e.target.value)} /></div>
        <div><Label className="text-xs">City</Label>
          <Input value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">State</Label>
            <Input value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
          <div><Label className="text-xs">ZIP</Label>
            <Input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
        </div>
        <div><Label className="text-xs">Time Zone</Label>
          <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
            <SelectTrigger data-testid="onb-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz.split("/")[1].replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Currency</Label>
          <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["USD", "CAD", "EUR", "GBP", "AUD", "ILS"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold" data-testid="onb-save-business-info">
        {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Save Business Info
      </Button>
    </div>
  );
};

/* ─── Embedded: Business Hours editor ─── */
const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const DEFAULT_DAY = { closed: false, open_24h: false, open: "08:00", close: "18:00" };

const BusinessHoursForm = ({ onSaved }) => {
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/onboarding/business-hours`, authCfg())
      .then((r) => {
        const h = r.data.hours || {};
        const init = {};
        DAYS.forEach(([k]) => { init[k] = { ...DEFAULT_DAY, ...(h[k] || {}), ...(k === "sat" || k === "sun" ? (h[k] ? {} : { closed: true }) : {}) }; });
        setHours(init);
      })
      .catch(() => setHours(Object.fromEntries(DAYS.map(([k]) => [k, { ...DEFAULT_DAY, closed: k === "sat" || k === "sun" }]))));
  }, []);
  const setDay = (k, patch) => setHours((h) => ({ ...h, [k]: { ...h[k], ...patch } }));
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/onboarding/business-hours`, { hours }, authCfg());
      toast.success("Business hours saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save hours");
    } finally { setSaving(false); }
  };
  if (!hours) return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>;
  return (
    <div className="space-y-2" data-testid="onb-business-hours-form">
      {DAYS.map(([k, label]) => (
        <div key={k} className="flex items-center gap-2 sm:gap-3 rounded-lg border border-border px-3 py-2 flex-wrap">
          <span className="text-xs font-medium w-20 text-foreground">{label}</span>
          <div className="flex items-center gap-1.5">
            <Switch checked={!hours[k].closed} onCheckedChange={(v) => setDay(k, { closed: !v })} data-testid={`onb-hours-${k}-open`} />
            <span className="text-[11px] text-muted-foreground w-10">{hours[k].closed ? "Closed" : "Open"}</span>
          </div>
          {!hours[k].closed && (
            <>
              <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <input type="checkbox" checked={hours[k].open_24h} onChange={(e) => setDay(k, { open_24h: e.target.checked })} className="accent-[#FFCC00]" /> 24h
              </label>
              {!hours[k].open_24h && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <Input type="time" value={hours[k].open} onChange={(e) => setDay(k, { open: e.target.value })} className="h-8 w-[105px] text-xs" />
                  <span className="text-muted-foreground text-xs">–</span>
                  <Input type="time" value={hours[k].close} onChange={(e) => setDay(k, { close: e.target.value })} className="h-8 w-[105px] text-xs" />
                </div>
              )}
            </>
          )}
        </div>
      ))}
      <Button onClick={save} disabled={saving} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold" data-testid="onb-save-hours">
        {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Save Business Hours
      </Button>
    </div>
  );
};

/* ─── Main wizard ─── */
export const OnboardingWizard = ({ open, onClose, initialStatus }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(initialStatus || null);
  const [screen, setScreen] = useState("welcome"); // welcome | steps | complete
  const [idx, setIdx] = useState(0);
  const [rechecking, setRechecking] = useState(false);

  const steps = status?.steps || [];
  const step = steps[idx];

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/onboarding/status`, authCfg());
      setStatus(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchStatus().then((s) => {
      if (!s) return;
      if (s.wizard?.started_at) {
        setScreen("steps");
        const pos = s.wizard.position ? s.steps.findIndex((x) => x.id === s.wizard.position) : -1;
        setIdx(pos >= 0 ? pos : Math.max(0, s.steps.findIndex((x) => x.status === "pending")));
      } else {
        setScreen("welcome");
      }
    });
  }, [open, fetchStatus]);

  if (!open || !status) return null;

  const wizardAction = (action, stepId) =>
    axios.post(`${API}/onboarding/wizard`, { action, step: stepId }, authCfg()).catch(() => {});

  const savePosition = (i) => {
    if (steps[i]) wizardAction("position", steps[i].id);
  };

  const startSetup = () => { wizardAction("start"); setScreen("steps"); setIdx(0); };
  const doLater = () => { wizardAction("later"); onClose(); };
  const goTo = (i) => {
    if (i >= steps.length) { setScreen("complete"); wizardAction("complete"); return; }
    setIdx(Math.max(0, i)); savePosition(Math.max(0, i));
  };
  const openLink = (link) => { savePosition(idx); onClose(); navigate(link); };
  const recheck = async () => {
    setRechecking(true);
    const s = await fetchStatus();
    setRechecking(false);
    if (s?.steps?.[idx]?.status === "complete") toast.success("Step complete — nice work!");
    else toast.info("Not detected yet — finish the configuration and check again");
  };
  const skip = async (mode) => {
    await axios.post(`${API}/onboarding/step/${step.id}/skip`, { mode }, authCfg()).catch(() => {});
    await fetchStatus();
    goTo(idx + 1);
  };
  const setIntent = async (value) => {
    await axios.post(`${API}/onboarding/step/${step.id}/intent`, { value }, authCfg()).catch(() => {});
    await fetchStatus();
    toast.success("Got it — step marked complete");
  };

  const trial = status.trial || {};
  const TrialChip = trial.active ? (
    <Badge variant="outline" className="border-[#FFCC00]/40 text-[#b8930a] dark:text-[#FFCC00] bg-[#FFCC00]/10 text-[11px]" data-testid="onb-trial-chip">
      Trial · {trial.days_remaining} day{trial.days_remaining === 1 ? "" : "s"} remaining
    </Badge>
  ) : null;

  const StepIcon = step ? (STEP_ICONS[step.id] || Circle) : Circle;

  return (
    <div className="fixed inset-0 z-[90] bg-background flex flex-col" data-testid="onboarding-wizard">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <span className="font-bold text-foreground font-['Manrope']">Fieldz<span className="text-[#FFCC00]">AI</span> Setup</span>
          {TrialChip}
        </div>
        <div className="flex items-center gap-3">
          {screen === "steps" && (
            <div className="hidden sm:flex items-center gap-2 w-48">
              <Progress value={status.percent} className="h-1.5" />
              <span className="text-xs text-muted-foreground tabular-nums" data-testid="onb-percent">{status.percent}%</span>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={() => { savePosition(idx); wizardAction("later"); onClose(); }} data-testid="onb-save-exit">
            <X className="w-4 h-4 mr-1" /> Save & Exit
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex">
        {/* Sidebar checklist (desktop, steps screen) */}
        {screen === "steps" && (
          <aside className="hidden md:block w-72 border-r border-border overflow-y-auto p-4 shrink-0">
            <p className="text-xs text-muted-foreground mb-1">Account Setup — {status.percent}% complete</p>
            <Progress value={status.percent} className="h-1.5 mb-4" />
            <div className="space-y-0.5">
              {steps.map((s, i) => {
                const Icon = STEP_ICONS[s.id] || Circle;
                return (
                  <button key={s.id} onClick={() => goTo(i)} data-testid={`onb-nav-${s.id}`}
                    className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${i === idx ? "bg-[#FFCC00]/15 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50"}`}>
                    {s.status === "complete"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      : s.skip_mode === "dont_need"
                        ? <MinusCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        : <Icon className="w-3.5 h-3.5 shrink-0" />}
                    <span className="truncate">{s.title}</span>
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        {/* Main area */}
        <main className="flex-1 overflow-y-auto">
          {screen === "welcome" && (
            <div className="max-w-lg mx-auto px-6 py-14 text-center" data-testid="onb-welcome-screen">
              <div className="w-16 h-16 rounded-2xl bg-[#FFCC00]/15 flex items-center justify-center mx-auto mb-5">
                <Sparkles className="w-8 h-8 text-[#FFCC00]" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-['Manrope'] mb-3">Welcome to Fieldz AI</h1>
              <p className="text-muted-foreground text-sm mb-2">Let's get your business ready.</p>
              <p className="text-muted-foreground text-sm mb-6">
                We'll guide you through the important settings so you can start creating jobs, communicating with
                customers, dispatching technicians, sending invoices and accepting payments.
              </p>
              <div className="rounded-xl border border-border bg-muted/30 p-4 mb-8 text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{status.total_steps} steps · about 10 minutes</span>
                  <span className="text-xs font-semibold text-foreground tabular-nums">{status.percent}% done</span>
                </div>
                <Progress value={status.percent} className="h-2" />
                {status.completed_steps > 0 && (
                  <p className="text-[11px] text-emerald-600 mt-2">{status.completed_steps} step{status.completed_steps === 1 ? " is" : "s are"} already complete — we detected your existing configuration.</p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button onClick={startSetup} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold px-8" data-testid="onb-start-setup">
                  Start Setup <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button variant="outline" onClick={doLater} data-testid="onb-do-later">I'll Do This Later</Button>
              </div>
            </div>
          )}

          {screen === "steps" && step && (
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid={`onb-step-${step.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[11px] text-muted-foreground">Step {idx + 1} of {steps.length}</span>
                <Badge variant="outline" className={`text-[10px] ${CATEGORY_BADGE[step.category]?.cls}`}>{CATEGORY_BADGE[step.category]?.label}</Badge>
                {step.status === "complete" && (
                  <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20" data-testid="onb-step-complete-badge">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                  </Badge>
                )}
                {step.skip_mode === "dont_need" && step.status !== "complete" && (
                  <Badge variant="outline" className="text-[10px]">Marked not needed</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#FFCC00]/15 flex items-center justify-center shrink-0">
                  <StepIcon className="w-5 h-5 text-[#b8930a] dark:text-[#FFCC00]" />
                </div>
                <h2 className="text-xl font-bold text-foreground font-['Manrope']">{step.title}</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-6">{step.description}</p>

              {/* Step body */}
              {step.id === "business_info" && <BusinessInfoForm onSaved={async () => { await fetchStatus(); }} />}
              {step.id === "business_hours" && <BusinessHoursForm onSaved={async () => { await fetchStatus(); }} />}
              {step.kind === "link" && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {step.link && (
                      <Button onClick={() => openLink(step.link)} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold" data-testid="onb-open-page">
                        <ExternalLink className="w-4 h-4 mr-1.5" /> Open {step.title.replace(/^(Create Your First|Configure|Add|Connect) /, "")}
                      </Button>
                    )}
                    {step.intent_option && step.status !== "complete" && (
                      <Button variant="outline" onClick={() => setIntent(step.intent_option.value)} data-testid="onb-intent-btn">
                        {step.intent_option.label}
                      </Button>
                    )}
                    <Button variant="outline" onClick={recheck} disabled={rechecking} data-testid="onb-recheck">
                      <RefreshCw className={`w-4 h-4 mr-1.5 ${rechecking ? "animate-spin" : ""}`} /> I've done this — check
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Opening the page saves your progress — come back to the Setup Guide from the dashboard anytime.
                    This step completes automatically once we detect the configuration.
                  </p>
                </div>
              )}

              {/* Footer nav */}
              <div className="flex items-center justify-between mt-10 pt-4 border-t border-border">
                <Button variant="ghost" onClick={() => goTo(idx - 1)} disabled={idx === 0} data-testid="onb-back">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <div className="flex gap-2">
                  {step.status !== "complete" && (
                    <Button variant="ghost" className="text-muted-foreground" onClick={() => skip("skip_for_now")} data-testid="onb-skip-now">
                      Skip for Now
                    </Button>
                  )}
                  {step.status !== "complete" && step.category === "optional" && (
                    <Button variant="ghost" className="text-muted-foreground" onClick={() => skip("dont_need")} data-testid="onb-dont-need">
                      I Don't Need This
                    </Button>
                  )}
                  <Button onClick={() => goTo(idx + 1)} className="bg-foreground text-background hover:opacity-90" data-testid="onb-continue">
                    Continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {screen === "complete" && (
            <div className="max-w-lg mx-auto px-6 py-14 text-center" data-testid="onb-complete-screen">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 flex items-center justify-center mx-auto mb-5">
                <PartyPopper className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-foreground font-['Manrope'] mb-2">Your Fieldz AI Account Is Ready 🎉</h1>
              <p className="text-sm text-muted-foreground mb-6">Setup is {status.percent}% complete.</p>
              <div className="rounded-xl border border-border bg-muted/30 p-4 mb-6 text-left space-y-1.5">
                {steps.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-xs">
                    {s.status === "complete"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      : s.skip_mode === "dont_need"
                        ? <MinusCircle className="w-3.5 h-3.5 text-muted-foreground" />
                        : <Circle className="w-3.5 h-3.5 text-muted-foreground" />}
                    <span className={s.status === "complete" ? "text-foreground" : "text-muted-foreground"}>{s.title}</span>
                  </div>
                ))}
              </div>
              {steps.some((s) => s.status !== "complete" && s.skip_mode !== "dont_need") && (
                <p className="text-[11px] text-muted-foreground mb-4">
                  Anything left above stays in the Setup Guide on your dashboard — finish it whenever you're ready.
                </p>
              )}
              <Button onClick={onClose} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold px-8" data-testid="onb-go-dashboard">
                Go to Dashboard
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default OnboardingWizard;
